import { historicalTables, sameHistoricalIdentity } from './HistoricalSnapshotHydrationService';
import { stableStringify } from '../checklist/ChecklistSnapshotCreationService';

type Row = Record<string, any>;
interface Dependencies {
  fetch(table: string, filters: Record<string, string>): Promise<Row[]>;
  enabled(): Promise<boolean>;
}

// Resolve apenas a referência de transporte; o payload original da fila permanece imutável.
export const resolveHistoricalValueCreateReference = async (
  payload: Row, scope: { companyId: string; userId: string }, deps: Dependencies
): Promise<{ payload: Row; historical: boolean } | null> => {
  if (!await deps.enabled()) return { payload, historical: false };
  if (payload.empresa_id !== scope.companyId || !payload.checklist_id || !payload.id) return null;
  if (!payload.campo_id) return { payload, historical: false };
  const logicalId = payload.campo_id;
  const current = await deps.fetch('guincho_campos_template', { id: logicalId, empresa_id: scope.companyId });
  if (current.length) {
    return current.length === 1 && current[0].id === logicalId
      && current[0].empresa_id === scope.companyId
      ? { payload, historical: false } : null;
  }
  const parents = await deps.fetch(historicalTables.parents, {
    id: payload.checklist_id, empresa_id: scope.companyId,
  });
  const parent = parents[0];
  if (parents.length !== 1 || parent.id !== payload.checklist_id || parent.empresa_id !== scope.companyId
    || !parent.usuario_id || parent.retired_at || !parent.snapshot_id) return null;
  const snapshots = await deps.fetch(historicalTables.snapshots, {
    id: parent.snapshot_id, checklist_id: parent.id, empresa_id: scope.companyId,
  });
  const snapshot = snapshots[0];
  if (snapshots.length !== 1 || !sameHistoricalIdentity(parent, snapshot)) return null;
  const schemas = await deps.fetch(historicalTables.schemas, {
    id: snapshot.template_schema_version_id, empresa_id: scope.companyId, template_id: parent.template_id,
  });
  const schema = schemas[0];
  const object = (value: any) => typeof value === 'string' ? JSON.parse(value) : value;
  if (schemas.length !== 1 || schema.id !== snapshot.template_schema_version_id
    || schema.empresa_id !== scope.companyId || schema.template_id !== parent.template_id
    || !schema.schema_hash || schema.schema_hash !== snapshot.schema_hash
    || stableStringify(object(schema.schema_payload)) !== stableStringify(object(snapshot.schema_payload))) return null;
  const fields = await deps.fetch(historicalTables.fields, {
    snapshot_id: snapshot.id, original_campo_id: logicalId, empresa_id: scope.companyId,
  });
  const field = fields[0];
  const schemaPayload = object(snapshot.schema_payload);
  const expected = Array.isArray(schemaPayload?.fields)
    ? schemaPayload.fields.filter((row: Row) => row.original_campo_id === logicalId) : [];
  if (fields.length !== 1 || !field.id || field.snapshot_id !== snapshot.id
    || field.empresa_id !== scope.companyId || field.original_campo_id !== logicalId
    || schemaPayload.template_id !== parent.template_id || expected.length !== 1
    || expected[0].field_key !== field.field_key || !field.field_key
    || (expected[0].id != null && expected[0].id !== field.id)
    || (payload.snapshot_field_id != null && payload.snapshot_field_id !== field.id)) return null;
  return { payload: { ...payload, campo_id: null, snapshot_field_id: field.id }, historical: true };
};

export const historicalValueCreateMatches = (payload: Row, remote: Row): boolean => Boolean(remote)
  && Object.entries(payload).every(([key, value]) => {
    if (key === 'created_at' || key === 'updated_at') {
      const timestamp = (input: any) => typeof input === 'number' ? input : Date.parse(input);
      return Number.isFinite(timestamp(value)) && timestamp(value) === timestamp(remote[key]);
    }
    return stableStringify(value ?? null) === stableStringify(remote[key] ?? null);
  });
